import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  HelpCircle,
  Image as ImageIcon,
  Info,
  Layers,
  Lightbulb,
  Menu,
  Plus,
  Search,
  FileText,
  X,
} from 'lucide-react';
import SupportContactSection from './SupportContactSection';
import AIChatWidget from '../ui/AIChatWidget';
import BetaFeedbackWidget from '../feedback/BetaFeedbackWidget';
import { currentRuntime } from '../../config/runtime';
import { resolveTenantProductName, type WhiteLabelAppBranding } from '../../lib/whiteLabelBranding';

type CalloutTone = 'info' | 'tip' | 'warning';

type DocumentationCallout = {
  tone: CalloutTone;
  title: string;
  body: string;
};

type DocumentationExample = {
  title: string;
  lines: string[];
};

type DocumentationMedia = {
  title: string;
  caption: string;
  src?: string;
  lightSrc?: string;
  darkSrc?: string;
};

type DocumentationSubsection = {
  id: string;
  title: string;
  body: string[];
  tips?: string[];
  callouts?: DocumentationCallout[];
  media?: DocumentationMedia;
  example?: DocumentationExample;
};

type DocumentationCategory = {
  id: string;
  title: string;
  description: string;
  subsections: DocumentationSubsection[];
};

const brandId = currentRuntime.brand.id || 'trade-proof';

const buildDocumentationCategories = (brandName: string): DocumentationCategory[] => [
  {
    id: 'getting-started',
    title: 'Getting Started',
    description: 'Set up your workspace and learn the core review flow.',
    subsections: [
      {
        id: 'workspace-basics',
        title: 'Workspace basics',
        body: [
          `${brandName} is organized around a simple loop: import trades, review the dashboard, inspect analytics, replay execution, and refine your tags and settings.`,
          'The Dashboard gives you the fastest read on ACCOUNT METRICS, recent performance, weekly results, active widgets, and the areas that need review.',
        ],
        tips: ['Start with one clean import before tuning tags or analytics filters.'],
        callouts: [{ tone: 'info', title: 'Best first session', body: 'Import trades from your broker using a CSV file upload or Broker Link, verify timestamps and symbols, then open Performance Overview before adjusting your chart settings.' }],
        media: {
          title: 'Dashboard workspace preview',
          caption: 'Dashboard overview with weekly summary and performance widgets.',
          lightSrc: brandId === 'warrior-trading' ? '/images/Dashboard-Warrior-Light.jpg' : '/images/Dashboard-Light.jpg',
          darkSrc: brandId === 'warrior-trading' ? '/images/Dashboard-Warrior-Dark.jpg' : '/images/Dashboard.jpg',
        },
      },
      {
        id: 'navigation-model',
        title: 'Navigation model',
        body: [
          'Primary navigation moves between Dashboard, Calendar, Trends, Trades, Journal, Search, Import Trades, Settings, and Support.',
          'The top menu and sidebar both expose the same Support destination, so help remains available regardless of layout preference.',
        ],
      },
    ],
  },
  {
    id: 'importing-trades',
    title: 'Importing Trades',
    description: `Bring CSV, fills, executions, and broker exports into ${brandName}.`,
    subsections: [
      {
        id: 'csv-imports',
        title: 'CSV imports',
        body: [
          `Export your trades, executions, or fills from your broker or trading platform as a CSV file, then upload that file into ${brandName}.`,
          'Imported trades are saved in this workspace on this browser. Availability on other devices may depend on your account setup and enabled server features.',
          'Import Trades supports standard CSV workflows and maps common trade, execution, symbol, quantity, price, timestamp, and P&L fields.',
          'For the most accurate replay and execution review, export an Executions or Fills report from your broker whenever possible.',
        ],
        callouts: [{ tone: 'warning', title: 'Timestamp precision matters', body: 'Daily summaries can populate ACCOUNT METRICS, but replay quality depends on intraday timestamps and execution-level rows.' }],
        example: {
          title: 'Recommended columns',
          lines: ['Symbol, Side, Quantity, Entry Price, Exit Price', 'Open Time, Close Time, Commission, Fees, P&L', 'Broker, Account, Strategy, Tags'],
        },
      },
      {
        id: 'import-review',
        title: 'Import review',
        body: [
          'After import, verify symbols, asset class, side, quantity, date, and P&L. Rejected rows can be reviewed from Import Rejections when diagnostics are available.',
          'Use tags immediately after import to keep setups, mistakes, sessions, and playbook labels consistent across analytics pages.',
        ],
      },
      {
        id: 'duplicate-reimports',
        title: 'Duplicate reimports',
        body: [
          'Reimporting the same file should not create duplicate trades or executions.',
          `${brandName} skips already-imported rows where possible, so it is safe to upload the same export again while you verify the result.`,
        ],
      },
    ],
  },
  {
    id: 'broker-sync',
    title: 'Broker Sync',
    description: 'Use read-only broker connections where available.',
    subsections: [
      {
        id: 'read-only-sync',
        title: 'Read-only sync',
        body: [
          `Broker Sync is designed for read-only access so ${brandName} can retrieve account and execution data without placing trades.`,
          'Synced data improves replay, execution markers, and broker-reported P&L reconciliation compared with incomplete CSV exports.',
        ],
        callouts: [{ tone: 'info', title: 'Security posture', body: `${brandName} should only request the minimum broker permissions required for reporting, imports, and analytics.` }],
      },
      {
        id: 'broker-coverage',
        title: 'Broker coverage',
        body: [
          'Supported broker coverage can expand over time. CSV import remains the fallback path for unsupported platforms.',
          'If a broker export does not include fills or executions, import the highest-detail report available and use chart settings to control what appears during review.',
        ],
      },
    ],
  },
  {
    id: 'dashboard-overview',
    title: 'Dashboard Overview',
    description: 'Understand the main control surface for daily trading review.',
    subsections: [
      {
        id: 'account-metrics',
        title: 'ACCOUNT METRICS',
        body: [
          'ACCOUNT METRICS summarize account-level performance, trading activity, and recent behavior in one scannable view.',
          'Use the weekly cards and dashboard widgets to identify whether results are being driven by trade quality, frequency, sizing, or specific sessions.',
        ],
        media: {
          title: 'Account metrics card layout',
          caption: 'Session Efficiency Heatmap (ACCOUNT METRICS).',
          lightSrc: '/images/Heatmap-Light.jpg',
          darkSrc: '/images/Heatmap-Dark.jpg',
        },
      },
      {
        id: 'dashboard-widgets',
        title: 'Dashboard widgets',
        body: [
          'Widgets can surface recent trades, volume, long-term investments, and other focused views. Layout presets help restore a known workspace quickly.',
          'Keep the dashboard compact: use analytics pages for deeper study and reserve the dashboard for the most useful daily signals.',
        ],
        tips: ['Use Layout Presets after finding a dashboard arrangement you want to keep.'],
      },
    ],
  },
  {
    id: 'analytics-pages',
    title: 'Analytics Pages',
    description: 'Read performance, behavior, tags, and time-based trading patterns.',
    subsections: [
      {
        id: 'performance-overview',
        title: 'PERFORMANCE OVERVIEW',
        body: [
          'PERFORMANCE OVERVIEW consolidates equity curves, drawdown, expectancy, risk behavior, and strategy-level performance.',
          'Use the asset-class selector to switch between your enabled asset classes without changing the overall analytics workflow.',
        ],
      },
      {
        id: 'win-vs-loss-days',
        title: 'WIN VS. LOSS DAYS',
        body: [
          'WIN VS. LOSS DAYS compares winning and losing sessions so you can see whether large losses, weak win rate, or inconsistency are affecting the account.',
          'Look for day-level behavior: overtrading after losses, outsized red days, or strong green days concentrated in one setup.',
        ],
      },
      {
        id: 'tag-patterns',
        title: 'TAG PATTERNS',
        body: [
          'TAG PATTERNS turns setups, mistakes, emotions, time windows, and playbook labels into measurable performance groups.',
          'Consistent tags make the difference between useful analytics and noisy analytics.',
        ],
        callouts: [{ tone: 'tip', title: 'Tag discipline', body: 'Prefer a small set of reusable tags over many one-off labels. Analytics improves when tags are consistent.' }],
      },
    ],
  },
  {
    id: 'trade-replay',
    title: 'Trade Replay',
    description: 'Replay trades with execution context and chart overlays.',
    subsections: [
      {
        id: 'replay-flow',
        title: 'Replay flow',
        body: [
          'Replay helps you re-experience the trade instead of only reading the final P&L. It is built for execution review, decision quality, and post-trade notes.',
          'Use playback controls with chart settings to reveal trade markers, execution markers, drawings, and P&L overlays at the right level of detail.',
        ],
        media: {
          title: 'Replay chart media area',
          caption: 'Replay view with execution markers, drawings, and playback controls.',
          lightSrc: '/images/Replay-Light.jpg',
          darkSrc: '/images/Replay-Dark.jpg',
        },
      },
      {
        id: 'execution-review',
        title: 'Execution review',
        body: [
          'Execution markers show how the position was built and reduced. This is useful for scaling behavior, partial exits, slippage checks, and broker fill review.',
          'When execution data is not available, replay can still use trade-level entry and exit markers from imported summary rows.',
        ],
      },
    ],
  },
  {
    id: 'tags-organization',
    title: 'Tags & Organization',
    description: 'Keep strategies, mistakes, and review notes measurable.',
    subsections: [
      {
        id: 'tagging-system',
        title: 'Tagging system',
        body: [
          'Use tags for setup names, market conditions, mistakes, emotions, sessions, and review outcomes.',
          'Tags appear across analytics views and help connect individual trades to repeatable behavior.',
        ],
        example: {
          title: 'Useful tag groups',
          lines: ['Setup: Opening Range, Pullback, Breakout', 'Mistake: Chased, Moved Stop, Early Exit', 'Session: Premarket, London, New York AM'],
        },
      },
      {
        id: 'journal-organization',
        title: 'Journal organization',
        body: [
          'The Journal is best used for written context: plan quality, execution notes, screenshots, and end-of-day review.',
          'Use Search when you need to find a symbol, tag, trade note, or older setup quickly.',
        ],
      },
    ],
  },
  {
    id: 'chart-features',
    title: 'Chart Features',
    description: 'Control chart markers, drawings, overlays, and indicators.',
    subsections: [
      {
        id: 'chart-settings',
        title: 'Chart settings',
        body: [
          'Chart Configuration controls default indicators, trade markers, trade drawings, execution markers, P&L overlay, legends, playback speed, and scroll behavior.',
          'These settings shape the replay and review experience without changing imported trade records.',
        ],
        tips: ['Turn on Show executions for fill-level review. Turn on Show P&L overlay when reviewing trade management decisions.'],
      },
      {
        id: 'markers-overlays',
        title: 'Markers and overlays',
        body: [
          'Trade markers identify entry and exit points. Execution markers show individual fills. The P&L overlay adds profit/loss context directly to the chart.',
          'Drawings are useful for marking planned levels, invalidation, supply/demand, or review observations.',
        ],
      },
    ],
  },
  {
    id: 'settings',
    title: 'Settings',
    description: 'Configure account, preferences, charting, billing, and admin access.',
    subsections: [
      {
        id: 'preferences',
        title: 'Preferences',
        body: [
          'Preferences include theme selection and locked workspace settings that remain consistent across sessions.',
          'Theme changes apply instantly and are used throughout dashboard, analytics, import, billing, and documentation surfaces.',
        ],
      },
      {
        id: 'charting-setup',
        title: 'Charting setup',
        body: [
          'Charting Setup stores defaults for replay and chart review. The reset action restores platform defaults when needed.',
          'Fee defaults and risk rules help normalize imported data and support cleaner analytics.',
        ],
      },
    ],
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    description: 'Resolve common import, sync, replay, and display issues.',
    subsections: [
      {
        id: 'import-errors',
        title: 'Import errors',
        body: [
          'If rows are rejected, check required fields first: symbol, side, quantity, timestamp, and price or P&L values.',
          'Download rejection diagnostics when available and compare the rejected rows to the source broker export.',
        ],
        callouts: [{ tone: 'warning', title: 'Avoid editing raw exports too aggressively', body: 'Changing date formats, removing headers, or renaming columns can make broker exports harder to parse.' }],
      },
      {
        id: 'pnl-reconciliation',
        title: 'P&L reconciliation',
        body: [
          'Small P&L differences can come from broker-reported values, fees, commissions, partial fills, contract multipliers, and rounding.',
          `When you compare ${brandName} to the broker statement, treat the broker-reported figures as the reconciliation reference.`,
        ],
      },
      {
        id: 'fees-commissions',
        title: 'Fees & commissions',
        body: [
          'Net P&L depends on whether fees and commissions were present in the imported data.',
          `If the source file omits cost fields, ${brandName} may only be able to show gross results or an incomplete net value.`,
        ],
      },
      {
        id: 'missing-replay-data',
        title: 'Missing replay data',
        body: [
          'Replay needs timestamps and symbol history. If replay appears sparse, verify the trade symbol, asset class, open time, close time, and broker export detail.',
          'Replay and chart data may use historical or delayed market data from third-party providers or supported broker/local connections. Availability can vary by symbol, market, timeframe, provider availability, account limits, and imported trade detail.',
          'Replay is available on the 1m, 5m, 15m, 30m, and 1H timeframes. The Daily (1D) timeframe provides chart context only — replay is not available on 1D.',
          `For futures, replay/chart data can be unavailable when required market data is not connected, the exact contract cannot be resolved, or the imported symbol does not provide enough contract detail. When that happens, ${brandName} should show an unavailable state instead of fake chart data.`,
          'For example, with IBKR this can happen when TWS/Gateway is not connected or a specific futures contract cannot be matched.',
          'Broker Sync or execution-level exports usually produce better replay fidelity than summary-only reports.',
        ],
      },
    ],
  },
  {
    id: 'security-privacy',
    title: 'Security & Privacy',
    description: 'Understand data access, account safety, and privacy posture.',
    subsections: [
      {
        id: 'account-security',
        title: 'Account security',
        body: [
          'Keep login credentials secure, use strong passwords, and avoid sharing browser sessions on public machines.',
          'Broker connections should remain read-only and limited to reporting workflows.',
        ],
        callouts: [{ tone: 'info', title: 'Read-only access', body: `${brandName} documentation and UI should treat broker sync as a reporting connection, not a trading interface.` }],
      },
      {
        id: 'data-privacy',
        title: 'Data privacy',
        body: [
          'Imported trades, notes, tags, and settings are sensitive trading records. Review them the same way you would account statements.',
          'Use Support for product issues, but avoid sending unnecessary personal data when a rejection diagnostic or screenshot is enough.',
        ],
      },
    ],
  },
  {
    id: 'faq',
    title: 'FAQ',
    description: 'Quick answers to common platform questions.',
    subsections: [
      {
        id: 'faq-brokers-supported',
        title: 'Which brokers and platforms are supported?',
        body: [
          `${brandName} supports direct integration with selected brokers and platforms, with CSV import available for many additional workflows.`,
          'Supported broker coverage will continue to expand over time.',
        ],
      },
      {
        id: 'faq-broker-not-supported',
        title: `Can I still use ${brandName} if my broker is not supported directly?`,
        body: [
          'Yes. If direct broker sync is not available, you can still import your execution data using CSV and use the full review, replay, and analytics workflow.',
        ],
      },
      {
        id: 'faq-csv-import',
        title: 'How does CSV import work?',
        body: [
          `Export your execution data from your broker or platform, upload the file into ${brandName}, and the system will normalize the data for chart review, replay, and performance analysis.`,
        ],
      },
      {
        id: 'faq-asset-classes',
        title: `Which asset classes can I review in ${brandName}?`,
        body: [`${brandName} supports workflows across the asset classes enabled for your account configuration.`],
      },
      {
        id: 'faq-replay-without-sync',
        title: 'Can I use replay without broker sync?',
        body: ['Yes. Replay can work from imported records, but execution-level exports or Broker Sync provide better detail for fills, markers, and intraday review.'],
      },
      {
        id: 'faq-trial-included',
        title: 'What is included in the 7-day free trial?',
        body: [
          `The trial includes full access to ${brandName}'s core workflow, including imports, on-chart review, replay, and analytics.`,
        ],
      },
      {
        id: 'faq-trial-replay',
        title: 'Does the replay engine work during the free trial?',
        body: ['Yes. Candle-by-candle replay is included during the 7-day trial.'],
      },
      {
        id: 'faq-basic-journal',
        title: `How is ${brandName} different from a basic trading journal?`,
        body: [
          `${brandName} goes beyond static journaling by combining broker or CSV imports, on-chart execution review, candle-by-candle replay, and behavioral analytics in one workflow.`,
        ],
      },
      {
        id: 'faq-switch-billing',
        title: 'Can I switch between monthly and annual billing later?',
        body: [
          'Yes. You can change your billing preference from monthly to annual, or annual to monthly, from your account settings.',
        ],
      },
      {
        id: 'faq-cancel-anytime',
        title: 'Can I cancel anytime?',
        body: [
          'Yes. There are no long-term contracts, and you can cancel before renewal at any time.',
        ],
      },
      {
        id: 'faq-tags',
        title: 'How many tags should I use?',
        body: ['Use enough tags to describe repeated behavior, but avoid one-off labels. A compact, consistent tag system produces cleaner TAG PATTERNS analytics.'],
      },
      {
        id: 'faq-data-secure',
        title: 'Is my trade data secure?',
        body: [
          `${brandName} is designed to handle your execution data with care, controlled access, and secure account-level workflows.`,
        ],
      },
    ],
  },
];

const calloutStyles: Record<CalloutTone, { shell: string; icon: React.ReactNode }> = {
  info: {
    shell: 'border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-100',
    icon: <Info className="h-4 w-4" />,
  },
  tip: {
    shell: 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-100',
    icon: <Lightbulb className="h-4 w-4" />,
  },
  warning: {
    shell: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-500/25 dark:bg-amber-500/10 dark:text-amber-100',
    icon: <AlertTriangle className="h-4 w-4" />,
  },
};

const normalizeText = (value: string) => value.toLowerCase().trim();

const categoryMatchesQuery = (category: DocumentationCategory, query: string) => {
  if (!query) return true;
  const haystack = [
    category.title,
    category.description,
    ...category.subsections.flatMap((subsection) => [
      subsection.title,
      ...subsection.body,
      ...(subsection.tips ?? []),
      ...(subsection.callouts ?? []).flatMap((callout) => [callout.title, callout.body]),
      subsection.media?.title ?? '',
      subsection.media?.caption ?? '',
      subsection.example?.title ?? '',
      ...(subsection.example?.lines ?? []),
    ]),
  ].join(' ');
  return normalizeText(haystack).includes(query);
};

type DocumentationSearchHit = {
  id: string;
  categoryId: string;
  categoryTitle: string;
  title: string;
  snippet: string;
  kind: 'category' | 'topic';
};

const subsectionMatchesQuery = (subsection: DocumentationSubsection, query: string) => {
  if (!query) return true;
  const haystack = [
    subsection.title,
    ...subsection.body,
    ...(subsection.tips ?? []),
    ...(subsection.callouts ?? []).flatMap((callout) => [callout.title, callout.body]),
    subsection.media?.title ?? '',
    subsection.media?.caption ?? '',
    subsection.example?.title ?? '',
    ...(subsection.example?.lines ?? []),
  ].join(' ');
  return normalizeText(haystack).includes(query);
};

const snippetForSubsection = (subsection: DocumentationSubsection, query: string) => {
  const candidates = [
    ...subsection.body,
    ...(subsection.tips ?? []),
    ...(subsection.callouts ?? []).flatMap((callout) => [callout.body]),
    subsection.media?.caption ?? '',
  ].filter(Boolean);

  const match = candidates.find((value) => normalizeText(value).includes(query));
  return match ?? subsection.body[0] ?? '';
};

function DocumentationSearch({
  query,
  setQuery,
  results,
  onSelect,
  inputRef,
}: {
  query: string;
  setQuery: (query: string) => void;
  results: DocumentationSearchHit[];
  onSelect: (hit: DocumentationSearchHit) => void;
  inputRef: React.RefObject<HTMLInputElement>;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const trimmedQuery = query.trim();

  useEffect(() => {
    const onMouseDown = (event: MouseEvent) => {
      if (!containerRef.current) return;
      if (event.target instanceof Node && containerRef.current.contains(event.target)) return;
      setOpen(false);
    };

    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <label className="relative block">
        <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400 dark:text-zinc-500" />
        <input
          ref={inputRef}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              setQuery('');
              setOpen(false);
            }
          }}
            className="h-12 w-full rounded-lg border border-zinc-200 bg-white pl-11 pr-20 text-sm text-zinc-900 shadow-sm outline-none transition-colors placeholder:text-zinc-400 focus:border-blue-500/60 focus:ring-2 focus:ring-blue-500/10 dark:border-zinc-800 dark:bg-zinc-950/60 dark:text-zinc-100 dark:placeholder:text-zinc-600"
          placeholder="Search documentation..."
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <button
            type="button"
            onClick={() => {
              setQuery('');
              setOpen(false);
              try { inputRef.current?.focus(); } catch {}
            }}
            className="inline-flex h-8 items-center rounded-lg border border-zinc-200 bg-white px-2.5 text-[11px] font-semibold text-zinc-600 shadow-sm transition-colors hover:bg-zinc-50 disabled:pointer-events-none disabled:opacity-40 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800"
          >
            Clear
          </button>
        </div>
      </label>

      {open && trimmedQuery ? (
        <div className="absolute left-0 right-0 z-50 mt-2 overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-xl dark:border-zinc-800 dark:bg-zinc-950">
          <div className="flex items-center justify-between border-b border-zinc-200 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500 dark:border-zinc-800">
            <span>Results</span>
            <span>{results.length}</span>
          </div>
          <div className="max-h-[320px] overflow-y-auto p-2">
            {results.length ? (
              results.map((hit) => (
                <button
                  key={hit.id}
                  type="button"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    onSelect(hit);
                    setOpen(false);
                  }}
                  className="w-full rounded-xl px-3 py-2 text-left transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-900"
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 shrink-0 text-zinc-400 dark:text-zinc-500">
                      {hit.kind === 'category' ? <Layers className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{hit.title}</div>
                      {hit.snippet ? (
                        <div className="mt-1 line-clamp-2 text-xs leading-5 text-zinc-500 dark:text-zinc-400">{hit.snippet}</div>
                      ) : null}
                      <div className="mt-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-400 dark:text-zinc-500">
                        {hit.categoryTitle}
                      </div>
                    </div>
                  </div>
                </button>
              ))
            ) : (
              <div className="px-3 py-6 text-sm text-zinc-500 dark:text-zinc-400">No matches found.</div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function DocumentationCalloutBlock({ callout }: { callout: DocumentationCallout }) {
  const style = calloutStyles[callout.tone];
  return (
    <div className={`mt-4 flex gap-3 rounded-lg border p-4 ${style.shell}`}>
      <div className="mt-0.5 shrink-0">{style.icon}</div>
      <div>
        <div className="text-sm font-semibold">{callout.title}</div>
        <p className="mt-1 text-sm leading-6 opacity-85">{callout.body}</p>
      </div>
    </div>
  );
}

function DocumentationImageBlock({ media }: { media: DocumentationMedia }) {
  const [isDark, setIsDark] = useState<boolean>(() => typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));
  const resolvedSrc = isDark ? (media.darkSrc ?? media.src ?? media.lightSrc) : (media.lightSrc ?? media.src ?? media.darkSrc);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const updateTheme = () => setIsDark(document.documentElement.classList.contains('dark'));
    const observer = new MutationObserver(() => updateTheme());

    try {
      observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    } catch {
      return;
    }

    updateTheme();

    return () => {
      try {
        observer.disconnect();
      } catch {}
    };
  }, []);

  return (
    <figure className="mt-5 overflow-hidden rounded-2xl border border-zinc-200 bg-zinc-50 shadow-sm dark:border-zinc-800 dark:bg-zinc-950/70">
      {resolvedSrc ? (
        <div className="overflow-hidden bg-zinc-950">
          <img src={resolvedSrc} alt={media.title} className="block w-full object-cover" loading="lazy" />
        </div>
      ) : (
        <div className="flex aspect-[16/7] min-h-[180px] items-center justify-center bg-[linear-gradient(135deg,rgba(59,130,246,0.08),rgba(15,23,42,0.02))] p-6 dark:bg-[linear-gradient(135deg,rgba(59,130,246,0.12),rgba(24,24,27,0.6))]">
          <div className="flex flex-col items-center text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-zinc-200 bg-white text-blue-600 shadow-sm dark:border-zinc-800 dark:bg-zinc-900 dark:text-blue-400">
              <ImageIcon className="h-5 w-5" />
            </div>
            <div className="mt-3 text-sm font-semibold text-zinc-900 dark:text-zinc-100">{media.title}</div>
            <div className="mt-1 max-w-md text-xs leading-5 text-zinc-500 dark:text-zinc-500">Screenshot, GIF, or video embed ready</div>
          </div>
        </div>
      )}
      <figcaption className="border-t border-zinc-200 px-4 py-3 text-xs leading-5 text-zinc-500 dark:border-zinc-800 dark:text-zinc-400">
        {media.caption}
      </figcaption>
    </figure>
  );
}

function DocumentationSection({
  category,
  highlighted,
}: {
  category: DocumentationCategory;
  highlighted: boolean;
}) {
  const isFaq = category.id === 'faq';

  return (
    <section
      id={category.id}
      data-doc-section
      className={`scroll-mt-24 rounded-lg border bg-white p-5 shadow-sm transition-all dark:bg-zinc-900 sm:p-6 ${
        highlighted
          ? 'border-blue-300 ring-2 ring-blue-500/10 dark:border-blue-500/50 dark:ring-blue-500/15'
          : 'border-zinc-200 dark:border-zinc-800'
      }`}
    >
      <div className="flex items-start gap-3 border-b border-zinc-200 pb-4 dark:border-zinc-800">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400">
          <BookOpen className="h-4 w-4" />
        </div>
        <div>
          <h2 className="text-xl font-bold tracking-tight text-zinc-950 dark:text-white">{category.title}</h2>
          <p className="mt-1 text-sm leading-6 text-zinc-500 dark:text-zinc-400">{category.description}</p>
        </div>
      </div>

      {isFaq ? (
        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
          {category.subsections.map((subsection) => (
              <article
              key={subsection.id}
              id={subsection.id}
              className="rounded-2xl border border-blue-500/60 bg-zinc-100 p-5 shadow-sm transition-colors dark:border-blue-500/50 dark:bg-zinc-800"
            >
              <div className="flex items-center gap-3">
                <div className="shrink-0 text-[26px] font-bold leading-none tracking-[-0.04em] text-blue-500">
                  Q
                </div>
                <div className="min-w-0">
                  <h3 className="text-[13px] font-bold uppercase leading-none tracking-[0.18em] text-zinc-900 dark:text-zinc-100">
                    {subsection.title}
                  </h3>
                </div>
              </div>
              <div className="mt-4 border-l border-zinc-200 pl-4 dark:border-zinc-800">
                {subsection.body.map((paragraph) => (
                  <p key={paragraph} className="text-sm leading-7 text-zinc-600 dark:text-zinc-300">
                    {paragraph}
                  </p>
                ))}
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="mt-5 space-y-7">
          {category.subsections.map((subsection) => (
            <article key={subsection.id} id={subsection.id} className="scroll-mt-24">
              <h3 className="text-sm font-bold uppercase tracking-[0.16em] text-zinc-900 dark:text-zinc-100">{subsection.title}</h3>
              <div className="mt-3 space-y-3">
                {subsection.body.map((paragraph) => (
                  <p key={paragraph} className="text-sm leading-7 text-zinc-600 dark:text-zinc-300">{paragraph}</p>
                ))}
              </div>
              {subsection.tips?.length ? (
                <div className="mt-4 grid gap-2">
                  {subsection.tips.map((tip) => (
                    <div key={tip} className="flex items-start gap-2 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-600 dark:border-zinc-800 dark:bg-zinc-950/60 dark:text-zinc-300">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
                      <span>{tip}</span>
                    </div>
                  ))}
                </div>
              ) : null}
              {subsection.callouts?.map((callout) => (
                <DocumentationCalloutBlock key={`${subsection.id}-${callout.title}`} callout={callout} />
              ))}
              {subsection.example ? (
                <details className="mt-4 rounded-xl border border-zinc-200 bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-950/60">
                  <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-zinc-800 marker:text-zinc-400 dark:text-zinc-100">
                    {subsection.example.title}
                  </summary>
                  <div className="border-t border-zinc-200 px-4 py-3 dark:border-zinc-800">
                    <pre className="overflow-x-auto rounded-lg bg-white p-3 text-xs leading-6 text-zinc-700 dark:bg-zinc-900 dark:text-zinc-300">
                      {subsection.example.lines.join('\n')}
                    </pre>
                  </div>
                </details>
              ) : null}
              {subsection.media ? <DocumentationImageBlock media={subsection.media} /> : null}
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function DocumentationSidebar({
  categories,
  activeId,
  onJump,
}: {
  categories: DocumentationCategory[];
  activeId: string;
  onJump: (id: string) => void;
}) {
  return (
    <nav className="space-y-2">
      {categories.map((category) => {
        const active = activeId === category.id;
        return (
          <div key={category.id} className="border-b border-zinc-200/80 dark:border-zinc-800/80">
            <button
              type="button"
              onClick={() => onJump(category.id)}
              className={`support-category-button flex w-full items-center gap-2 rounded-none px-3 py-2.5 text-left text-sm transition-colors ${
                active
                  ? 'bg-blue-50 pl-7 font-bold text-blue-700 dark:bg-blue-500/10 dark:text-blue-300'
                  : 'text-zinc-700 hover:bg-zinc-50 hover:text-zinc-950 dark:text-zinc-300 dark:hover:bg-zinc-900 dark:hover:text-white'
              }`}
              style={{ borderRadius: 0 }}
            >
              <Plus className="h-4 w-4 flex-shrink-0" />
              <span className={active ? 'font-bold' : 'font-medium'}>{category.title}</span>
            </button>
          </div>
        );
      })}
    </nav>
  );
}

type SupportPageProps = {
  whiteLabel?: WhiteLabelAppBranding | null;
  accountContext?: {
    uid?: string | null;
    email?: string | null;
  } | null;
};

export function SupportPage({ accountContext = null, whiteLabel = null }: SupportPageProps) {
  const brandName = resolveTenantProductName();
  const documentationCategories = useMemo(() => buildDocumentationCategories(brandName), [brandName]);
  const [query, setQuery] = useState('');
  const [activeId, setActiveId] = useState(documentationCategories[0].id);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [showContactPanel, setShowContactPanel] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const normalizedQuery = normalizeText(query);
  const filteredCategories = useMemo(
    () => documentationCategories.filter((category) => categoryMatchesQuery(category, normalizedQuery)),
    [normalizedQuery],
  );
  const totalCategoryCount = documentationCategories.length;
  const totalGuideCount = documentationCategories.reduce((total, category) => total + category.subsections.length, 0);

  const searchHits = useMemo(() => {
    if (!normalizedQuery) return [] as DocumentationSearchHit[];

    const hits: DocumentationSearchHit[] = [];
    documentationCategories.forEach((category) => {
      const categoryHaystack = normalizeText([category.title, category.description].join(' '));
      if (categoryHaystack.includes(normalizedQuery)) {
        hits.push({
          id: `category-${category.id}`,
          kind: 'category',
          categoryId: category.id,
          categoryTitle: category.title,
          title: category.title,
          snippet: category.description,
        });
      }

      category.subsections.forEach((subsection) => {
        if (!subsectionMatchesQuery(subsection, normalizedQuery)) return;
        hits.push({
          id: `topic-${category.id}-${subsection.id}`,
          kind: 'topic',
          categoryId: category.id,
          categoryTitle: category.title,
          title: subsection.title,
          snippet: snippetForSubsection(subsection, normalizedQuery),
        });
      });
    });

    return hits.slice(0, 8);
  }, [normalizedQuery]);

  const activeCategory = useMemo(
    () => filteredCategories.find((category) => category.id === activeId) ?? filteredCategories[0] ?? null,
    [activeId, filteredCategories],
  );

  useEffect(() => {
    if (!filteredCategories.length) return;
    if (!filteredCategories.some((category) => category.id === activeId)) {
      setActiveId(filteredCategories[0].id);
    }
  }, [activeId, filteredCategories]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      if (key !== 'k') return;
      if (!(event.metaKey || event.ctrlKey)) return;
      event.preventDefault();
      searchInputRef.current?.focus();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const selectCategory = (id: string) => {
    setMobileNavOpen(false);
    setShowContactPanel(false);
    setActiveId(id);
  };

  return (
    <div className="min-h-screen bg-zinc-100 px-4 py-8 font-sans text-zinc-900 dark:bg-zinc-950 dark:text-zinc-100 sm:px-6 lg:px-8">
      <div className="mx-auto w-full" style={{ maxWidth: '1480px' }}>
        <header className="mb-6 rounded-lg border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-900 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-blue-600 dark:text-blue-400">
              <HelpCircle className="h-4 w-4" />
              Documentation & Help
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {currentRuntime.features.regularCustomerFeedback ? (
                <BetaFeedbackWidget
                  tenantId={currentRuntime.tenant.id}
                  source="REGULAR_CUSTOMER"
                  triggerLabel="Send Feedback"
                  placement="support"
                />
              ) : null}
              <button
                type="button"
                onClick={() => setShowContactPanel(true)}
                className="inline-flex h-11 shrink-0 items-center rounded-xl border border-blue-500 bg-white px-4 text-sm font-semibold text-zinc-700 shadow-sm transition-colors hover:bg-blue-50 hover:text-zinc-950 dark:border-blue-500 dark:bg-zinc-950/40 dark:text-zinc-200 dark:hover:bg-blue-500/10 dark:hover:text-white"
              >
                Contact Support
              </button>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(360px,480px)] lg:items-center">
            <div className="max-w-3xl">
              <h1 className="text-3xl font-bold tracking-tight text-zinc-950 dark:text-white sm:text-4xl">{brandName} knowledge center</h1>
              <p className="mt-3 text-sm leading-6 text-zinc-500 dark:text-zinc-400">
                Practical platform documentation for imports, broker sync, dashboard review, analytics, replay, chart settings, tags, security, and troubleshooting.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <div className="inline-flex items-center gap-2 rounded-lg border border-zinc-200 bg-white px-4 py-2 text-sm font-semibold text-zinc-700 shadow-sm dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-200">
                  <Layers className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  {totalCategoryCount} Categories
                </div>
                <div className="inline-flex items-center gap-2 rounded-lg border border-zinc-200 bg-white px-4 py-2 text-sm font-semibold text-zinc-700 shadow-sm dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-200">
                  <BookOpen className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                  {totalGuideCount} Guides
                </div>
              </div>
            </div>

            <DocumentationSearch
              query={query}
              setQuery={setQuery}
              results={searchHits}
              onSelect={(hit) => selectCategory(hit.categoryId)}
              inputRef={searchInputRef}
            />
          </div>
        </header>

        <div className="mb-4 flex items-center justify-between gap-3 lg:hidden">
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            className="inline-flex h-10 items-center gap-2 rounded-xl border border-zinc-200 bg-white px-4 text-sm font-semibold text-zinc-700 shadow-sm transition-colors hover:bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
          >
            <Menu className="h-4 w-4" />
            Sections
          </button>
        </div>

        {mobileNavOpen ? (
          <div className="fixed inset-0 z-[9999] lg:hidden">
            <button type="button" aria-label="Close documentation navigation" className="absolute inset-0 bg-black/40" onClick={() => setMobileNavOpen(false)} />
            <div className="absolute left-0 top-0 h-full w-[min(88vw,360px)] overflow-y-auto border-r border-zinc-200 bg-zinc-100 p-4 shadow-2xl dark:border-zinc-800 dark:bg-zinc-950">
              <div className="mb-4 flex items-center justify-between">
                <div className="text-sm font-bold text-zinc-950 dark:text-white">Documentation</div>
                <button type="button" onClick={() => setMobileNavOpen(false)} className="rounded-lg p-2 text-zinc-500 hover:bg-zinc-200 hover:text-zinc-900 dark:hover:bg-zinc-800 dark:hover:text-white">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <DocumentationSidebar categories={filteredCategories} activeId={activeId} onJump={selectCategory} />
            </div>
          </div>
        ) : null}

        <div className="grid grid-cols-1 items-stretch gap-5 lg:grid-cols-[280px_minmax(0,1fr)]">
          <aside className="hidden lg:block">
            <div className="sticky top-24 h-full max-h-[calc(100vh-7rem)] overflow-y-auto rounded-lg border border-zinc-200 bg-zinc-50/80 p-3 shadow-sm dark:border-zinc-800 dark:bg-zinc-900/70">
              <DocumentationSidebar categories={filteredCategories} activeId={activeId} onJump={selectCategory} />
            </div>
          </aside>

          <main className="min-w-0 space-y-5">
            {showContactPanel ? (
              <SupportContactSection titleId="support-contact-panel-title" className="h-full" accountContext={accountContext} />
            ) : activeCategory ? (
              <DocumentationSection key={activeCategory.id} category={activeCategory} highlighted={Boolean(normalizedQuery && categoryMatchesQuery(activeCategory, normalizedQuery))} />
            ) : (
              <section className="rounded-2xl border border-zinc-200 bg-white p-8 text-center shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-zinc-100 text-zinc-500 dark:bg-zinc-950 dark:text-zinc-500">
                  <Search className="h-5 w-5" />
                </div>
                <h2 className="mt-4 text-lg font-bold text-zinc-950 dark:text-white">No documentation found</h2>
                <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-zinc-500 dark:text-zinc-400">
                  Try searching for imports, broker sync, ACCOUNT METRICS, PERFORMANCE OVERVIEW, WIN VS. LOSS DAYS, TAG PATTERNS, replay, chart settings, or execution markers.
                </p>
              </section>
            )}

          </main>
        </div>
      </div>
      <AIChatWidget whiteLabel={whiteLabel} />
    </div>
  );
}

export default SupportPage;
